import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SupplierController from './controllers/supplier-controller';
import BrandController from './controllers/brand-controller';
import VariantController from './controllers/variant-controller';
import TVAController from './controllers/tva-controller';
import { toast , ToastContainer } from 'react-toastify';
import AddSupplierModal from './components/addSupplierModal';
import Modal from '../../components/Modal';

// Import component modules
import BasicProductInfo from './new-product-components/BasicProductInfo';
import PricingInfo from './new-product-components/PricingInfo';
import StockInfo from './new-product-components/StockInfo';
import SupplierDropdown from './new-product-components/SupplierDropdown';
import BrandDropdown from './new-product-components/BrandDropdown';
import TvaDropdown from './new-product-components/TvaDropdown';
import ProductImageUploader from './new-product-components/ProductImageUploader';
import VariantsSection from './new-product-components/VariantsSection';
import VariantsTable from './new-product-components/VariantsTable';
import PackProductSection from './new-product-components/PackProductSection';
import { getAllReferences } from './controllers/products-controller';
import { use } from 'react';
import { useAtom } from 'jotai';
import { tokenAtom } from '../../atoms/authAtoms'; // Import the token atom at the top with other imports

const BASEURL = process.env.REACT_APP_API_URL

const AddProductPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [allReferences , setAllReferences] = useState([]);
  const [productName, setProductName] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [reference, setReference] = useState('');
  const [barcode, setBarcode] = useState('');
  const [price, setPrice] = useState('');
  const [prixAchatHT, setPrixAchatHT] = useState('');
  const [prixAchatTTC, setPrixAchatTTC] = useState('');
  const [prixVenteHT, setPrixVenteHT] = useState('');
  const [enPromo, setEnPromo] = useState(false); // Whether product is on promo
  const [prixVentePromo, setPrixVentePromo] = useState(''); // Promo price or code field
  const [livraisonGratuit, setLivraisonGratuit] = useState(false); // Whether product has free delivery
  const [livraisonGratuitQuantite, setLivraisonGratuitQuantite] = useState(''); // Number of products with free delivery
  const [stockRequis, setStockRequis] = useState(true); // Whether stock is needed to order the product
  const [stock, setStock] = useState(0);
  const [supplierId, setSupplierId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [tvaId, setTvaId] = useState('');
  const [token] = useAtom(tokenAtom);

  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [supplierError, setSupplierError] = useState(null);
  const [brands, setBrands] = useState([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [brandError, setBrandError] = useState(null);
  const [tvaRates, setTvaRates] = useState([]);
  const [isLoadingTva, setIsLoadingTva] = useState(false);
  const [tvaError, setTvaError] = useState(null);
  
  // Product images
  const [productImages, setProductImages] = useState([]);
  const [productImagePreviews, setProductImagePreviews] = useState([]);
  
  // Variants state
  const [enableVariants, setEnableVariants] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [generatedVariants, setGeneratedVariants] = useState([]);
  const [showGeneratedVariants, setShowGeneratedVariants] = useState(false);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [variantAttributes, setVariantAttributes] = useState([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [variantError, setVariantError] = useState(null);
  
  // Modal states
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierEmail, setNewSupplierEmail] = useState('');
  const [newSupplierTel, setNewSupplierTel] = useState('');
  const [newSupplierAddress, setNewSupplierAddress] = useState('');
  const [newSupplierFiscal, setNewSupplierFiscal] = useState('');
  const [isSubmittingSupplier, setIsSubmittingSupplier] = useState(false);
  const [isCreateBrandModalOpen, setIsCreateBrandModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [isSubmittingBrand, setIsSubmittingBrand] = useState(false);
  const [packProducts, setPackProducts] = useState([]);

  // Get selected objects
  const selectedSupplier = suppliers.find(s => s.id === supplierId);
  const selectedBrand = brands.find(b => b.id === brandId);
  const selectedTva = tvaRates.find(t => t.id === tvaId);

  // Toggle selection of variant values
  const toggleVariantValue = (attributeId, valueId) => {
    const variantKey = JSON.stringify({ attributeId, valueId });

    setSelectedVariants(prev => {
      const exists = prev.some(item =>
        JSON.stringify({ attributeId: item.attributeId, valueId: item.valueId }) === variantKey
      );

      if (exists) {
        return prev.filter(item =>
          JSON.stringify({ attributeId: item.attributeId, valueId: item.valueId }) !== variantKey
        );
      } else {
        return [...prev, { attributeId, valueId }];
      }
    });
  };

  // Check if a variant value is selected
  const isVariantValueSelected = (attributeId, valueId) => {
    return selectedVariants.some(
      item => item.attributeId === attributeId && item.valueId === valueId
    );
  };

  // Function to generate variants
  const generateVariants = () => {
    if (selectedVariants.length === 0) return;

    const selectedValuesByAttribute = {};

    selectedVariants.forEach(item => {
      const attributeId = String(item.attributeId);
      const valueId = String(item.valueId);

      if (!selectedValuesByAttribute[attributeId]) {
        selectedValuesByAttribute[attributeId] = [];
      }
      selectedValuesByAttribute[attributeId].push(valueId);
    });

    // Find attribute and value info
    const attributesWithValues = [];

    Object.entries(selectedValuesByAttribute).forEach(([attributeId, valueIds]) => {
      const attribute = variantAttributes.find(attr => String(attr.id) === attributeId);
      if (!attribute) {
        console.error(`Attribute with ID ${attributeId} not found`);
        return;
      }

      const values = [];
      for (const valueId of valueIds) {
        const value = attribute.values.find(v => String(v.id) === valueId);
        if (value) {
          values.push({
            id: valueId,
            value: value.value
          });
        }
      }

      if (values.length > 0) {
        attributesWithValues.push({
          id: attributeId,
          name: attribute.name,
          values
        });
      }
    });

    if (attributesWithValues.length === 0) {
      console.error("No attributes with selected values found");
      return;
    }

    // Generate all combinations recursively
    const generateCombinations = (attributes, index = 0, current = {}, currentDisplay = []) => {
      if (index === attributes.length) {
        return [{ options: { ...current }, displayValues: [...currentDisplay] }];
      }

      const currentAttr = attributes[index];
      const combinations = [];

      for (const val of currentAttr.values) {
        const newCurrent = { ...current };
        newCurrent[currentAttr.id] = {
          attributeId: currentAttr.id,
          attributeName: currentAttr.name,
          valueId: val.id,
          value: val.value
        };

        const newDisplay = [...currentDisplay, val.value];
        combinations.push(...generateCombinations(attributes, index + 1, newCurrent, newDisplay));
      }

      return combinations;
    };

    const combinations = generateCombinations(attributesWithValues);

    if (combinations.length === 0) {
      console.error("No combinations generated");
      return;
    }

    

    // Format variants for display and use
    const formattedVariants = combinations.map((combo, index) => {
      const displayName = combo.displayValues.join(' - ');

      return {
        id: `var-${Date.now()}-${index}`,
        combination: displayName,
        options: Object.values(combo.options),
        sku: reference ? `${reference}-${index + 1}` : `SKU-${index + 1}`,
        stock: 0,
        imageUrl: '',
        selected: false
      };
    });

    setGeneratedVariants(formattedVariants);
    setShowGeneratedVariants(true);
  };

  useEffect(() => {
    const fetchReferencesList = async () => {
      try {
        const response = await getAllReferences();

        if (response) {
          setAllReferences(response);
        } else {
          setAllReferences([]);
        }
      } catch (error) {
        console.error('Error fetching references:', error);
      } finally {
      }
    }

    fetchReferencesList();
  }, []);
function formatNumber(value) {
    return value.toFixed(2).replace('.', ','); // Ensure two decimal places and replace '.' with ','
}
  useEffect(() => {
    if (prixAchatTTC && tvaId) {
      const selectedTvaRate = tvaRates.find(t => t.id === tvaId);
      if (selectedTvaRate) {
        const tvaValue = selectedTvaRate.taux / 100;
        const calculatedPrixAchatHT = parseFloat(prixAchatTTC) / (1 + tvaValue);
        setPrixAchatHT(calculatedPrixAchatHT.toFixed(2));
      }
    } else {
      setPrixAchatHT('');
    }
  }, [prixAchatTTC, tvaId, tvaRates]);

  useEffect(() => {
    if (price && tvaId) {
      const selectedTvaRate = tvaRates.find(t => t.id === tvaId);
      if (selectedTvaRate) {
        const tvaValue = selectedTvaRate.taux / 100;
        const calculatedPrixVenteHT = parseFloat(price) / (1 + tvaValue);
        setPrixVenteHT(calculatedPrixVenteHT.toFixed(2));
      }
    } else {
      setPrixVenteHT('');
    }
  }, [price, tvaId, tvaRates]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchSuppliersList = async () => {
      try {
        setIsLoadingSuppliers(true);
        setSupplierError(null);
        const response = await SupplierController.fetchAllSuppliers();
        if (response) {
          setSuppliers(response);
        } else {
          setSuppliers([]);
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        setSupplierError('Impossible de charger la liste des fournisseurs');
      } finally {
        setIsLoadingSuppliers(false);
      }
    };

    const fetchBrandsList = async () => {
      try {
        setIsLoadingBrands(true);
        setBrandError(null);
        const data = await BrandController.fetchAllBrands();
        if (data) {
          setBrands(data);
        } else {
          setBrands([]);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        setBrandError('Impossible de charger la liste des marques');
      } finally {
        setIsLoadingBrands(false);
      }
    };

    const fetchVariantsList = async () => {
      try {
        setIsLoadingVariants(true);
        setVariantError(null);
        const response = await VariantController.fetchAllVariants();
        if (response) {
          setVariantAttributes(response);
        } else {
          setVariantAttributes([]);
        }
      } catch (error) {
        console.error('Error fetching variants:', error);
        setVariantError('Impossible de charger la liste des attributs de variante');
      } finally {
        setIsLoadingVariants(false);
      }
    };

    const fetchTvaRates = async () => {
      try {
        setIsLoadingTva(true);
        setTvaError(null);
        const data = await TVAController.fetchAllTva();
        if (data) {
          setTvaRates(data);
          // Don't log tvaRates here - it won't have updated yet
        } else {
          setTvaRates([]);
        }
      } catch (error) {
        console.error('Error fetching TVA rates:', error);
        setTvaError('Impossible de charger les taux de TVA');
      } finally {
        setIsLoadingTva(false);
      }
    };

    fetchSuppliersList();
    fetchBrandsList();
    fetchVariantsList();
    fetchTvaRates();
  }, []);

  useEffect(() => {
  }, [tvaRates]);

  // Update total stock when variant stocks change
  useEffect(() => {
    if (enableVariants && showGeneratedVariants && generatedVariants.length > 0) {
      const totalStock = generatedVariants.reduce((sum, variant) => {
        return sum + (parseInt(variant.stock, 10) || 0);
      }, 0);
      setStock(totalStock);
    }
  }, [enableVariants, showGeneratedVariants, generatedVariants]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
  
    // Check if reference already exists
    if (allReferences.includes(reference.trim())) {
      setError('Cette référence existe déjà');
      toast.error('Cette référence existe déjà dans la base de données');
      return;
    }
  
    // Basic validation - remove barcode, supplierId, brandId from required fields
    if (!productName || !price || !reference || 
      !prixAchatHT || !prixAchatTTC || !prixVenteHT || !tvaId) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
  
    try {
      setIsSubmitting(true);
      const formData = new FormData();
  
      // Add basic product info
      formData.append('Reference', reference);
      formData.append('Name', productName);
      formData.append('Description', productDesc || '');
      // Make barcode optional
      if (barcode) {
        formData.append('CodeABarre', barcode);
      } else {
        formData.append('CodeABarre', ''); // Send empty string for optional field
      }
      formData.append('PrixVenteHt', formatNumber(parseFloat(prixVenteHT)));
      formData.append('PrixVenteTTC', formatNumber(parseFloat(price)));
      formData.append('PrixAchatTTC', formatNumber(parseFloat(prixAchatTTC)));
      formData.append('PrixAchatHT', formatNumber(parseFloat(prixAchatHT)));
      // Promotion fields
      formData.append('EnPromo', enPromo ? 'true' : 'false');
      formData.append('PrixVentePromo', enPromo && prixVentePromo ? formatNumber(parseFloat(prixVentePromo)) : '0');
      formData.append('LivraisonGratuit', livraisonGratuit ? 'true' : 'false');
      formData.append('LivraisonGratuitQuantite', livraisonGratuit && livraisonGratuitQuantite ? parseInt(livraisonGratuitQuantite, 10) : '0');
      formData.append('StockRequis', stockRequis ? 'true' : 'false');

      // Make brand optional
      if (brandId) {
        formData.append('MarqueId', brandId);
      } else {
        formData.append('MarqueId', ''); // Send empty string for optional field
      }
      
      formData.append('TVAId', tvaId);
      
      // Make supplier optional
      if (supplierId) {
        formData.append('FournisseurId', supplierId);
      } else {
        formData.append('FournisseurId', ''); // Send empty string for optional field
      }
      
      if (enableVariants && showGeneratedVariants && generatedVariants.length > 0) {
        const totalStock = generatedVariants.reduce((sum, variant) => {
          return sum + (parseInt(variant.stock, 10) || 0);
        }, 0);
        formData.append('Stock', totalStock);
      } else {
        formData.append('Stock', parseInt(stock, 10));
      }
      
      formData.append('IsVariant', enableVariants && generatedVariants.length > 0);
  
      // Add pack products
      if (packProducts.length > 0) {
        const validPacks = packProducts.filter(p => p.qte && p.prixUnitaireTTC);
        if (validPacks.length > 0) {
          formData.append('PackProducts', JSON.stringify(validPacks.map(p => ({
            qte: parseInt(p.qte, 10),
            prixUnitaireTTC: parseFloat(p.prixUnitaireTTC)
          }))));
        }
      }

      // Add product images - now optional
      if (productImages.length > 0) {
        productImages.forEach(image => {
          formData.append('Images', image);
        });
      }
  
      // Handle variants
      if (enableVariants && showGeneratedVariants && generatedVariants.length > 0) {
        const variantsData = generatedVariants.map(v => ({
          reference: v.sku,
          name: v.combination,
          stock: parseInt(v.stock, 10),
          variantValue: v.options.map(opt => ({
            variantValueId: opt.valueId.toString()
          })),
          images: [],
          codeABarre: v.barCode || '',
        }));
        formData.append('ProductVariants', JSON.stringify(variantsData));
  
        // Add variant images
        generatedVariants.forEach((variant, variantIndex) => {
          if (variant.imageFiles && variant.imageFiles.length > 0) {
            variant.imageFiles.forEach((imgFile) => {
              const originalFile = imgFile;
              const filenameParts = originalFile.name.split('.');
              const extension = filenameParts.pop();
              const newFilename = `${variant.sku}`;
              const renamedFile = new File(
                [originalFile],
                newFilename,
                { type: originalFile.type }
              );
              formData.append(`VariantImages`, renamedFile);
            });
          }
        });
      }
  
      // Get token from localStorage as fallback if atom is empty
      const authToken = token || localStorage.getItem('authToken');
  
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
  
      // Send the data with Authorization header
      const response = await fetch(`${BASEURL}/product`, {
        method: 'POST',
        headers: {
          // Note: Don't set Content-Type for FormData requests, browser will set it with the boundary
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
  
      if (!response.ok) {
        // Handle unauthorized status specifically
        if (response.status === 401) {
          toast.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/login');
          return;
        }
        
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        throw new Error(`Error ${response.status}: ${JSON.stringify(errorData)}`);
      }
  
      // Show success message
      toast.success('Produit créé avec succès');
      navigate('/admin/products');
  
    } catch (err) {
      console.error('Error creating product:', err);
      setError(`Erreur lors de la création du produit: ${err.message || 'Erreur inconnue'}`);
      toast.error(`Erreur: ${err.message || 'Une erreur est survenue lors de la création du produit'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle supplier creation
  const handleSubmitNewSupplier = async (e) => {
    e.preventDefault();

    if (!newSupplierName.trim()) {
      toast.error('Le nom du fournisseur est requis');
      return;
    }

    setIsSubmittingSupplier(true);

    try {
      const createdSupplier = await SupplierController.createSupplier({
        name: newSupplierName.trim(),
        email: newSupplierEmail.trim(),
        tel: newSupplierTel.trim(),
        adresse: newSupplierAddress.trim(),
        matriculeFiscal: newSupplierFiscal.trim()
      });

      setIsCreateSupplierModalOpen(false);
      toast.success('Fournisseur créé avec succès');

      setNewSupplierName('');
      setNewSupplierEmail('');
      setNewSupplierTel('');
      setNewSupplierAddress('');
      setNewSupplierFiscal('');

      try {
        setIsLoadingSuppliers(true);
        const response = await SupplierController.fetchAllSuppliers();
        if (response) {
          setSuppliers(response);
          setSupplierId(createdSupplier.id);
        } else {
          setSuppliers(prev => [...prev, createdSupplier]);
          setSupplierId(createdSupplier.id);
        }
      } catch (error) {
        setSuppliers(prev => [...prev, createdSupplier]);
        setSupplierId(createdSupplier.id);
      } finally {
        setIsLoadingSuppliers(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la création du fournisseur');
    } finally {
      setIsSubmittingSupplier(false);
    }
  };

  // Handle brand creation
  const handleSubmitNewBrand = async (e) => {
    e.preventDefault();

    if (!newBrandName.trim()) {
      toast.error('Le nom de la marque est requis');
      return;
    }

    setIsSubmittingBrand(true);

    try {
      const createdBrand = await BrandController.createBrand({ name: newBrandName.trim() });
      setIsCreateBrandModalOpen(false);
      toast.success('Marque créée avec succès');
      setNewBrandName('');

      try {
        setIsLoadingBrands(true);
        const data = await BrandController.fetchAllBrands();
        if (data) {
          setBrands(data);
          setBrandId(createdBrand.id);
        } else {
          setBrands(prev => [...prev, createdBrand]);
          setBrandId(createdBrand.id);
        }
      } catch (error) {
        setBrands(prev => [...prev, createdBrand]);
        setBrandId(createdBrand.id);
      } finally {
        setIsLoadingBrands(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la création de la marque');
    } finally {
      setIsSubmittingBrand(false);
    }
  };

  // Component for brand form
  const CreateBrandForm = ({ brandName, setBrandName, isSubmitting }) => {
    return (
      <form id="createBrandForm" onSubmit={(e) => e.preventDefault()}>
        <div className="space-y-4">
          <div>
            <label htmlFor="brandName" className="block text-sm font-medium text-gray-700">
              Nom de la marque
            </label>
            <input
              type="text"
              name="brandName"
              id="brandName"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              disabled={isSubmitting}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
              placeholder="Entrez le nom de la marque"
              autoFocus
              required
            />
          </div>
        </div>
      </form>
    );
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ajouter un nouveau produit</h1>
          <p className="mt-2 text-sm text-gray-700">
            Créez un nouveau produit pour votre catalogue
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/admin/products"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux produits
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Informations du produit</h3>
          <p className="mt-1 text-sm text-gray-500">Les champs marqués d'un * sont obligatoires</p>
        </div>

        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="p-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            {/* BasicProductInfo component */}
            <BasicProductInfo 
              productName={productName}
              setProductName={setProductName}
              productDesc={productDesc}
              setProductDesc={setProductDesc}
              reference={reference}
              setReference={setReference}
              barcode={barcode}
              setBarcode={setBarcode}
              allReferences={allReferences}
            />
            
            {/* PricingInfo component */}
            <PricingInfo 
              price={price}
              setPrice={setPrice}
              prixAchatHT={prixAchatHT}
              prixAchatTTC={prixAchatTTC}
              setPrixAchatTTC={setPrixAchatTTC}
              prixVenteHT={prixVenteHT}
            />
            
            {/* Promo toggle and promo price/code input */}
            <div className="sm:col-span-3">
              <div className="relative flex items-start bg-gray-50 p-4 rounded-md border border-gray-200">
                <div className="flex items-center h-5">
                  <input
                    id="enPromo"
                    name="enPromo"
                    type="checkbox"
                    checked={enPromo}
                    onChange={(e) => setEnPromo(e.target.checked)}
                    className="focus:ring-[#174540] h-5 w-5 text-[#174540] border-gray-300 rounded transition-colors"
                  />
                </div>
                <div className="ml-3 text-sm w-full">
                  <label htmlFor="enPromo" className="font-medium text-gray-700">Mettre en promotion</label>
                  <p className="text-gray-500">Activez pour appliquer un prix promotionnel ou un code promo.</p>

                  <div className="mt-3">
                    <input
                      type="number"
                      name="prixVentePromo"
                      id="prixVentePromo"
                      min="0"
                      step="0.01"
                      value={prixVentePromo}
                      onChange={(e) => setPrixVentePromo(e.target.value)}
                      placeholder="Prix promo (ex: 9.99)"
                      disabled={!enPromo}
                      className={`mt-1 block w-full pl-3 py-2.5 sm:text-sm border border-gray-300 rounded-md transition-colors ${enPromo ? '' : 'bg-gray-100'}`}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Free delivery toggle and quantity input */}
            <div className="sm:col-span-3">
              <div className="relative flex items-start bg-gray-50 p-4 rounded-md border border-gray-200">
                <div className="flex items-center h-5">
                  <input
                    id="livraisonGratuit"
                    name="livraisonGratuit"
                    type="checkbox"
                    checked={livraisonGratuit}
                    onChange={(e) => setLivraisonGratuit(e.target.checked)}
                    className="focus:ring-[#174540] h-5 w-5 text-[#174540] border-gray-300 rounded transition-colors"
                  />
                </div>
                <div className="ml-3 text-sm w-full">
                  <label htmlFor="livraisonGratuit" className="font-medium text-gray-700">Livraison gratuite</label>
                  <p className="text-gray-500">Activez et indiquez le nombre de produits bénéficiant de la livraison gratuite.</p>

                  <div className="mt-3">
                    <input
                      type="number"
                      name="livraisonGratuitQuantite"
                      id="livraisonGratuitQuantite"
                      min="0"
                      step="1"
                      value={livraisonGratuitQuantite}
                      onChange={(e) => setLivraisonGratuitQuantite(e.target.value)}
                      placeholder="Nombre de produits (ex: 2)"
                      disabled={!livraisonGratuit}
                      className={`mt-1 block w-full pl-3 py-2.5 sm:text-sm border border-gray-300 rounded-md transition-colors ${livraisonGratuit ? '' : 'bg-gray-100'}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Stock required toggle */}
            <div className="sm:col-span-3">
              <div className="relative flex items-start bg-gray-50 p-4 rounded-md border border-gray-200">
                <div className="flex items-center h-5">
                  <input
                    id="stockRequis"
                    name="stockRequis"
                    type="checkbox"
                    checked={stockRequis}
                    onChange={(e) => setStockRequis(e.target.checked)}
                    className="focus:ring-[#174540] h-5 w-5 text-[#174540] border-gray-300 rounded transition-colors"
                  />
                </div>
                <div className="ml-3 text-sm w-full">
                  <label htmlFor="stockRequis" className="font-medium text-gray-700">Stock requis</label>
                  <p className="text-gray-500">
                    {stockRequis
                      ? 'Le client ne peut commander que si le produit est en stock.'
                      : 'Le client peut commander même sans stock, et le stock n’est pas affiché sur la boutique.'}
                  </p>
                </div>
              </div>
            </div>

            {/* StockInfo component */}
            <StockInfo 
              stock={stock}
              setStock={setStock}
              enableVariants={enableVariants}
              showGeneratedVariants={showGeneratedVariants}
              generatedVariants={generatedVariants}
            />
            
            {/* SupplierDropdown component */}
            <SupplierDropdown 
              suppliers={suppliers}
              supplierId={supplierId}
              setSupplierId={setSupplierId}
              isLoadingSuppliers={isLoadingSuppliers}
              supplierError={supplierError}
              setIsCreateSupplierModalOpen={setIsCreateSupplierModalOpen}
            />
            
            {/* BrandDropdown component */}
            <BrandDropdown 
              brands={brands}
              brandId={brandId}
              setBrandId={setBrandId}
              isLoadingBrands={isLoadingBrands}
              brandError={brandError}
              setIsCreateBrandModalOpen={setIsCreateBrandModalOpen}
            />
            
            {/* TvaDropdown component */}
            <TvaDropdown 
              tvaRates={tvaRates}
              tvaId={tvaId}
              setTvaId={setTvaId}
              isLoadingTva={isLoadingTva}
              tvaError={tvaError}
            />
            
            {/* ProductImageUploader component */}
            <ProductImageUploader 
              productImages={productImages}
              setProductImages={setProductImages}
              productImagePreviews={productImagePreviews}
              setProductImagePreviews={setProductImagePreviews}
            />
            
            {/* VariantsSection component */}
            <VariantsSection 
              enableVariants={enableVariants}
              setEnableVariants={setEnableVariants}
              isVariantModalOpen={isVariantModalOpen}
              setIsVariantModalOpen={setIsVariantModalOpen}
              variantAttributes={variantAttributes}
              selectedVariants={selectedVariants}
              toggleVariantValue={toggleVariantValue}
              isVariantValueSelected={isVariantValueSelected}
              generateVariants={generateVariants}
            />
            
            
            {/* VariantsTable component */}
            {enableVariants && showGeneratedVariants && generatedVariants.length > 0 && (
              <VariantsTable 
                generatedVariants={generatedVariants}
                setGeneratedVariants={setGeneratedVariants}
              />
            )}

            {/* PackProductSection component */}
            <PackProductSection
              packs={packProducts}
              setPacks={setPackProducts}
            />
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] mr-3"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Création...
                </>
              ) : 'Créer le produit'}
            </button>
          </div>
        </form>
      </div>

      <AddSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSubmit={handleSubmitNewSupplier}
        supplierName={newSupplierName}
        setSupplierName={setNewSupplierName}
        supplierEmail={newSupplierEmail}
        setSupplierEmail={setNewSupplierEmail}
        supplierTel={newSupplierTel}
        setSupplierTel={setNewSupplierTel}
        supplierAddress={newSupplierAddress}
        setSupplierAddress={setNewSupplierAddress}
        supplierFiscal={newSupplierFiscal}
        setSupplierFiscal={setNewSupplierFiscal}
        isSubmitting={isSubmittingSupplier}
      />

      {/* Create Brand Modal */}
      <Modal
        isOpen={isCreateBrandModalOpen}
        onClose={() => setIsCreateBrandModalOpen(false)}
        title="Créer une nouvelle marque"
        onSubmit={handleSubmitNewBrand}
        size="md"
        isSubmitting={isSubmittingBrand}
        submitLabel="Créer"
      >
        <CreateBrandForm
          brandName={newBrandName}
          setBrandName={setNewBrandName}
          isSubmitting={isSubmittingBrand}
        />
      </Modal>

    </div>
  );
};

export default AddProductPage;
